import { METADATA_PATHS } from './chunk-7NWIUGSG.js';
import { unwrapData } from './chunk-HIWBYL4O.js';
import { coerceNumber } from './chunk-STJDIADC.js';
import { globalSingleton } from './chunk-N7SZ66QO.js';
import { forensicCheck, isForensicLane, forensicParamOverride, forensicLanesLabel } from './chunk-O3OIBNP7.js';

// src/evidence/policy.ts
var LADDER_TIERS = ["base", "system", "customer", "org", "tenant", "team", "user"];
var WRITABLE_TIERS = LADDER_TIERS.filter(
  (t) => t !== "base" && t !== "system"
);
var GATE_ACTIONS = ["keep", "flag", "demote", "withhold"];
var GATE_ACTION_LABELS = {
  keep: "Keep (measure only)",
  flag: "Keep, marked",
  demote: "Keep, forced to low",
  withhold: "Drop"
};
var ACTION_VERBS = {
  keep: "keep",
  flag: "keep marked",
  demote: "force to low",
  withhold: "drop"
};
var GATE_ACTION_HELP = {
  keep: "Nothing is gated. The floor is measured and reported, so you can see what it would drop before turning it on.",
  flag: "The result is kept and marked low-confidence. Choose this when a weak reading is still worth seeing.",
  demote: "The result is kept but forced to the low tier, which is what most surfaces branch on.",
  withhold: "The result is dropped and never reaches a reader. A record of what was dropped, and why, is always kept."
};
var DOMAIN_LABELS = {
  abi_source: "ABI source trust",
  tx_semantic: "Transaction meaning",
  semantic_rollup: "Headline decode confidence",
  tx_grouping: "Transaction grouping",
  address_clustering: "Address clustering",
  metadata_resolution: "Field resolution",
  protocol_detection: "Protocol detection",
  protocol_version: "Protocol version",
  proxy: "Proxy resolution",
  nested_semantic: "Nested calldata",
  graph_resolution: "Entity resolution",
  third_party: "Third-party providers",
  entity_rotate: "Entity re-assignment",
  promotion: "Catalog promotion",
  feedback: "User corrections",
  risk: "Risk signals",
  pipeline_parse: "Parse enrichments",
  ml: "Model predictions",
  integrations: "Integration outputs",
  abi_override: "Uploaded ABIs",
  proxy_override: "Pinned proxies",
  catalog_discovery: "Catalog discovery",
  catalog_attribution: "Attribution on a transaction",
  seed_enrichment: "Seeded enrichments",
  address_forensics: "Forensic checks"
};
var DOMAIN_HELP = {
  abi_source: "How much a decode is trusted based on where its ABI came from \u2014 a verified upload versus a bytecode guess. This is the number every transaction row carries.",
  tx_semantic: "How sure we are what a transaction MEANS (a swap, a bridge send).",
  tx_grouping: "How sure we are that two transactions belong to the same activity.",
  address_clustering: "How sure we are that several addresses belong to one identity. Below the floor a cluster is dissolved back into single addresses.",
  proxy: "How sure we are which implementation a proxy contract points at.",
  risk: "How confident an enrichment must be before it can move a risk score.",
  ml: "How confident a model prediction must be before it is used.",
  integrations: "How confident an output from one of your integrations (your own code, run on each transaction) must be before it is added to the transaction. Below the floor the output is dropped and a record of why is kept.",
  third_party: "How far each external data provider is trusted.",
  metadata_resolution: "How sure we must be that a raw field maps to a canonical one before the field is stored. Below the floor it is not written, so no reader sees it. To hide weak names without losing data, raise 'Show field names from' instead.",
  pipeline_parse: "Confidence in the structural readings taken during processing.",
  catalog_attribution: "How sure a label, tag, entity or group must be before processing credits it to a transaction. Below the floor the claim is never written to the transaction or the warehouse \u2014 for everyone, and only from now on. To hide weak attribution from your own views without removing it from the data, raise 'Read confidence floor' instead.",
  catalog_discovery: "How far each discovery feed is trusted when it proposes a NEW catalog row. Distinct from the attribution floor above, which judges a row the catalog already holds.",
  address_forensics: "How much a forensic check's verdict is worth when you identify addresses with forensic checks \u2014 that an address is an exchange deposit address, or an address-poisoning lookalike \u2014 and how much stronger evidence (more sweeps, more victims) raises it. Whether a finding still goes to Review is decided by the check's source trust and your review thresholds."
};
var KNOB_KINDS = {
  catalog_read_floor: "probability",
  catalog_include_unscored: "boolean",
  confidence_floor: "probability",
  materialize_threshold: "probability",
  rotation_threshold: "probability",
  self_write_min_trust: "probability",
  corroborated_write_min_sources: "count",
  attribution_gate_enabled: "boolean",
  catalog_overwrite_existing: "boolean",
  min_sources_for_verified: "count",
  require_human_for_verified: "boolean",
  allow_automation_promotion: "boolean",
  agreement_per_source: "probability",
  agreement_max_boost: "probability",
  decay_half_life_days: "days",
  decay_floor: "probability",
  metadata_read_floor: "probability",
  risk_model_mode: "choice"
};
var KNOB_CHOICES = {
  risk_model_mode: [
    { value: "off", label: "Off (rules only)" },
    { value: "advisory", label: "Advisory (beside the rules)" },
    { value: "override", label: "Override (model leads)" }
  ]
};
function knobChoices(knob) {
  return KNOB_CHOICES[knob] ?? [];
}
var KNOB_LABELS = {
  catalog_read_floor: "Read confidence floor",
  catalog_include_unscored: "Include unscored findings",
  confidence_floor: "Minimum to record anything",
  materialize_threshold: "Trusted without review at",
  rotation_threshold: "Move an address between entities at",
  self_write_min_trust: "Sources may skip review from",
  corroborated_write_min_sources: "Sources that may write together",
  attribution_gate_enabled: "Apply the write floor to attribution",
  catalog_overwrite_existing: "Findings update existing items",
  min_sources_for_verified: "Independent sources required",
  require_human_for_verified: "Only a person can confirm",
  allow_automation_promotion: "Let automation confirm",
  agreement_per_source: "Boost per agreeing source",
  agreement_max_boost: "Most that agreement can add",
  decay_half_life_days: "Confidence half-life",
  decay_floor: "Confidence never decays below",
  metadata_read_floor: "Show field names from",
  risk_model_mode: "Model risk scores"
};
var KNOB_HELP = {
  catalog_read_floor: "Show catalog items, attributions, ABIs and decoded calls/events whose recorded score meets this floor. This changes reads; stored evidence and automated decisions are retained.",
  catalog_include_unscored: "Include findings without a recorded score. By default they are included only when the minimum is zero. Verification is a separate status.",
  confidence_floor: "Anything scoring below this is dropped \u2014 not written, not queued for review. The bottom of three bands: set it too high and weak findings vanish before anyone can look at them.",
  materialize_threshold: "At or above this, a finding from a source allowed to skip review (the next setting) is written as fact without anyone looking at it. Below it, or from any other source, it waits in Review. The line between 'shown to you as fact' and 'queued for someone to check'.",
  self_write_min_trust: "Whose word is enough. A source must be trusted at least this much before its findings may skip review. 80% admits sources that hold the fact or read the chain directly \u2014 verified contracts, a protocol's own service, explorers, nodes, our own registries; indexers (70%), vendor analytics (60%) and heuristics (30\u201340%) always need a person. Retune one source under Third-party providers. 100% means everything above the minimum is reviewed.",
  corroborated_write_min_sources: "When no single source is trusted enough to skip review, this many INDEPENDENT sources agreeing on the same finding may write it together \u2014 if their combined trust reaches the bar above and the finding scores at or above the trusted score. 0 turns corroborated writes off: every finding from a less-trusted source waits in Review.",
  rotation_threshold: "How sure we must be before re-assigning an address from one entity to another while it is live. Deliberately the highest bar on this page \u2014 getting it wrong rewrites history.",
  attribution_gate_enabled: "Whether processing applies the attribution floor when it credits an existing label, tag, entity or group to a transaction. On by default and inert until that floor is above zero; turning it off leaves the floor saved but stops it being applied.",
  catalog_overwrite_existing: "When a run finds an attribution or an address owner the catalog already holds: on, the finding is merged into it \u2014 its source joins the others and the score is recomputed; off, what is stored is kept exactly as it is and the finding only adds what is missing (one that would give an address a different owner goes to Review). Anything a person made or approved is never overwritten by a finding, whichever you choose.",
  min_sources_for_verified: "How many INDEPENDENT sources must agree before a finding can be trusted without review. The core false-positive control: 1 means a single source is enough; 2 or more means one provider can never confirm itself.",
  require_human_for_verified: "Nothing reaches 'confirmed' unless a person says so. The strictest setting on this page, and the one to reach for when a false positive is more expensive than a delay.",
  allow_automation_promotion: "Let our own detectors confirm a finding on their own. Off by default: a detector we run is still a machine agreeing with itself.",
  agreement_per_source: "How much confidence each additional agreeing source adds. Small on purpose \u2014 corroboration should nudge, not manufacture certainty.",
  agreement_max_boost: "The ceiling on that boost, and on the matching penalty when sources disagree. Stops a long tail of weak agreeing sources from adding up to a strong claim.",
  decay_half_life_days: "How long until a finding nobody has seen again is worth half as much. 0 turns decay off entirely, which is the default \u2014 turn it on if your data goes stale faster than it is re-observed.",
  decay_floor: "Decay never takes a finding below this, however old it gets. Keeps a once-verified fact from decaying into nothing.",
  metadata_read_floor: "In the explorer, dashboards and views, a stored field scoring below this is shown encoded \u2014 its raw name and value, no label \u2014 and left out of counts. Nothing is deleted: lower it and the names come back. A single view can raise it, never lower it.",
  risk_model_mode: "Whether a model's risk score \u2014 from a model we run or one you added, or a risk signal from one of your integrations \u2014 may decide a transaction's headline risk. Off: only the rules score risk. Advisory (the default): the model's score is shown beside the rules' and the headline stays the rules'. Override: a model score that cleared its floor leads the headline, with the rules' score kept beside it."
};
var KNOB_ORDER = [
  "confidence_floor",
  "materialize_threshold",
  "self_write_min_trust",
  "corroborated_write_min_sources",
  "attribution_gate_enabled",
  "catalog_overwrite_existing",
  "min_sources_for_verified",
  "require_human_for_verified",
  "allow_automation_promotion",
  "rotation_threshold",
  "agreement_per_source",
  "agreement_max_boost",
  "decay_half_life_days",
  "decay_floor",
  "risk_model_mode",
  "metadata_read_floor",
  "catalog_read_floor",
  "catalog_include_unscored"
];
function knobLabel(knob) {
  return KNOB_LABELS[knob] ?? knob;
}
function knobKind(knob) {
  if (isRiskPoints(knob)) return "points";
  if (/^risk_(signals|policies)\..+\.enabled$/.test(knob)) return "boolean";
  const forensic = forensicCheckPath(knob);
  if (forensic?.field === "enabled") return "boolean";
  if (forensic?.field === "param") return "number";
  if (forensic?.field === "lanes") return "lanes";
  return KNOB_KINDS[knob] ?? "probability";
}
function knobsToShow(vocabularyKnobs) {
  const known = KNOB_ORDER.filter((k) => vocabularyKnobs.includes(k));
  const rest = [...vocabularyKnobs].filter((k) => !KNOB_ORDER.includes(k)).sort();
  return [...known, ...rest];
}
function candidateSection(ownSection, edits) {
  const out = {};
  for (const [key, value] of Object.entries(ownSection ?? {})) {
    const copied = value && typeof value === "object" && !Array.isArray(value) ? { ...value } : value;
    out[key] = NUMERIC_SECTIONS.has(key) ? numericLeaves(copied) : copied;
  }
  for (const [path, value] of Object.entries(edits)) {
    const parts = policyPathParts(path);
    if (value === void 0) deletePath(out, parts);
    else setPath(out, parts, numericKnobValue(path, value));
  }
  return out;
}
var NUMERIC_SECTIONS = /* @__PURE__ */ new Set([
  "floors",
  "tier_cutoffs",
  "base_overrides"
]);
function numericKnobValue(path, value) {
  const numeric = NUMERIC_SECTIONS.has(path.split(".")[0] ?? "") || isRiskPoints(path) || forensicCheckPath(path)?.field === "param";
  if (!numeric) return value;
  return coerceNumber(value) ?? value;
}
var FORENSIC_CHECKS_SECTION = "forensic_checks";
function forensicEnabledPath(checkId) {
  return `${FORENSIC_CHECKS_SECTION}.${checkId}.enabled`;
}
function forensicLanesPath(checkId) {
  return `${FORENSIC_CHECKS_SECTION}.${checkId}.lanes`;
}
function forensicParamPath(checkId, name) {
  return `${FORENSIC_CHECKS_SECTION}.${checkId}.params.${name}`;
}
var FORENSIC_PATH = /^forensic_checks\.(.+?)(?:\.(enabled|lanes)|\.(params)(?:\.([^.]+))?)?$/;
function forensicCheckPath(path) {
  const match = FORENSIC_PATH.exec(path);
  if (!match) return null;
  const [, checkId, word, params, param] = match;
  if (!checkId) return null;
  if (word === "enabled" || word === "lanes") return { checkId, field: word };
  if (params) return param ? { checkId, field: "param", param } : { checkId, field: "params" };
  return { checkId, field: "entry" };
}
function policyPathParts(path) {
  const forensic = forensicCheckPath(path);
  if (!forensic) return path.split(".");
  const head = [FORENSIC_CHECKS_SECTION, forensic.checkId];
  if (forensic.field === "enabled" || forensic.field === "lanes") return [...head, forensic.field];
  if (forensic.field === "param") return [...head, "params", forensic.param];
  if (forensic.field === "params") return [...head, "params"];
  return head;
}
function forensicParamLabel(name) {
  const words = name.replace(/_/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : name;
}
function forensicCheckProblems(value) {
  if (value === void 0 || value === null) return [];
  if (typeof value !== "object" || Array.isArray(value)) {
    return [`${FORENSIC_CHECKS_SECTION} must be an object keyed by check id`];
  }
  const problems = [];
  for (const [checkId, entry] of Object.entries(value)) {
    const where = `${FORENSIC_CHECKS_SECTION}.${checkId}`;
    const check = forensicCheck(checkId);
    if (!check) {
      problems.push(`${where}: no such forensic check`);
      continue;
    }
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      problems.push(`${where} must be an object ({enabled?, lanes?, params?})`);
      continue;
    }
    for (const [key, raw] of Object.entries(entry)) {
      const path = `${where}.${key}`;
      if (key === "enabled") {
        if (typeof raw !== "boolean") problems.push(`${path} must be true or false`);
      } else if (key === "lanes") {
        if (!Array.isArray(raw) || !raw.every(isForensicLane)) {
          problems.push(`${path} must be a list of lanes (research, pipeline)`);
        }
      } else if (key !== "params") {
        problems.push(`${path}: not tunable (tunable: enabled, lanes, params)`);
      } else if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        problems.push(`${path} must be an object keyed by parameter name`);
      } else {
        for (const [name, number] of Object.entries(raw)) {
          const problem = forensicParamProblem(check.params[name], number);
          if (problem === null) continue;
          problems.push(
            problem === "unknown" ? `${path}.${name}: ${checkId} has no such parameter (it has: ${Object.keys(check.params).sort().join(", ") || "none"})` : `${path}.${name} ${problem}`
          );
        }
      }
    }
  }
  return problems;
}
function forensicParamProblem(param, raw) {
  if (!param) return "unknown";
  if (typeof raw !== "number") return "must be a number";
  if (!Number.isFinite(raw)) return "must be a finite number";
  if (param.integer && !Number.isInteger(raw)) return "must be a whole number";
  if (forensicParamOverride(param, raw) !== null) return null;
  return param.high === null ? `must be at least ${param.low}` : `must be between ${param.low} and ${param.high}`;
}
var RISK_SECTIONS = ["risk_signals", "risk_policies"];
var RISK_POINTS_MIN = 0;
var RISK_POINTS_MAX = 100;
var RISK_POINTS_FIELD = {
  risk_signals: "weight",
  risk_policies: "max_score"
};
function isRiskPoints(path) {
  const [section, , field] = path.split(".");
  return (section === "risk_signals" || section === "risk_policies") && field === RISK_POINTS_FIELD[section];
}
function numericLeaves(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, numericLeaves(v)])
    );
  }
  return coerceNumber(value) ?? value;
}
function ownSectionOf(ladder, tier) {
  const found = (ladder?.tiers ?? []).find(
    (t) => t.tier === tier
  );
  return found?.section ?? {};
}
function inheritedValue(ladder, path, tier) {
  const rank = (t) => LADDER_TIERS.indexOf(t);
  const mine = rank(tier);
  const wider = (ladder?.tiers ?? []).filter((t) => rank(t.tier) >= 0 && rank(t.tier) < mine).sort((a, b) => rank(b.tier) - rank(a.tier));
  for (const t of wider) {
    const node = authoredAt(t.section, path);
    if (node !== void 0) return { value: node, tier: t.tier };
  }
  return null;
}
function authoredAt(section, path) {
  let node = section;
  for (const key of policyPathParts(path)) {
    node = node && typeof node === "object" ? node[key] : void 0;
  }
  return node === null || node === "" ? void 0 : node;
}
function shadowedSettings(ladder, section, tier) {
  const rank = (t) => LADDER_TIERS.indexOf(t);
  const mine = rank(tier);
  const narrower = (ladder?.tiers ?? []).filter((t) => rank(t.tier) > mine).sort((a, b) => rank(a.tier) - rank(b.tier));
  const out = [];
  for (const path of sectionPaths(section)) {
    const setter = narrower.find((t) => authoredAt(t.section, path) !== void 0);
    if (setter) out.push({ path, tier: setter.tier });
  }
  return out;
}
function cutoffKey(path) {
  const [head, ...rest] = path.split(".");
  return head === "tier_cutoffs" && rest.length === 1 ? rest[0] ?? null : null;
}
function baseOverridePath(path) {
  const parts = path.split(".");
  if (parts[0] !== "base_overrides" || parts.length !== 3) return null;
  const [, domain, key] = parts;
  return domain && key ? { domain, key } : null;
}
var CUTOFF_LABELS = {
  high: "High confidence starts at",
  medium: "Medium confidence starts at"
};
var CUTOFF_HELP = {
  high: "At or above this a result is badged high. Processing and alert rules set their own thresholds; this badge does not authorize an action or establish accuracy.",
  medium: "At or above this a result is badged medium; below it, low. These labels organize review without changing scores or the rules that act on them."
};
var WEIGHT_TABLE_TITLES = {
  third_party: "How far we trust each data provider",
  abi_source: "How far we trust each ABI source"
};
function weightTableTitle(domain) {
  return WEIGHT_TABLE_TITLES[domain] ?? `${domainLabel(domain)} \u2014 weights`;
}
function weightRows(defaults, ladder, domain) {
  const seen = new Set(Object.keys(defaults ?? {}));
  for (const origin of ladder?.origins ?? []) {
    const parsed = baseOverridePath(origin.path);
    if (parsed?.domain === domain) seen.add(parsed.key);
  }
  return [...seen].sort();
}
function activitySummary(activity, domain, windowDays) {
  const bucket = activity?.[domain];
  const evaluated = bucket?.evaluated ?? 0;
  if (!bucket || evaluated <= 0) return null;
  const withheld = bucket.withhold ?? 0;
  const flagged = bucket.flag ?? 0;
  const demoted = bucket.demote ?? 0;
  const acted = withheld + flagged + demoted;
  const window = `in the last ${windowDays} day${windowDays === 1 ? "" : "s"}`;
  if (acted === 0) {
    return `${evaluated.toLocaleString()} judged, none gated ${window}`;
  }
  const parts = [];
  if (withheld) parts.push(`${withheld.toLocaleString()} dropped`);
  if (flagged) parts.push(`${flagged.toLocaleString()} marked`);
  if (demoted) parts.push(`${demoted.toLocaleString()} demoted`);
  return `${parts.join(", ")} of ${evaluated.toLocaleString()} judged ${window}`;
}
function activityWithheld(activity, domain) {
  return activity?.[domain]?.withhold ?? 0;
}
function floorDomain(path) {
  const [head, ...rest] = path.split(".");
  return head === "floors" && rest.length === 1 ? rest[0] ?? null : null;
}
function actionDomain(path) {
  const [head, ...rest] = path.split(".");
  return head === "gate_actions" && rest.length === 1 ? rest[0] ?? null : null;
}
function policyRowFor(path) {
  const forensic = forensicCheckPath(path);
  if (forensic && forensic.field !== "param" && forensic.field !== "lanes") {
    return `${FORENSIC_CHECKS_SECTION}.${forensic.checkId}`;
  }
  if (forensic) return path;
  const [channel, domain, key] = path.split(".");
  if ((channel === "floors" || channel === "gate_actions") && domain) return domain;
  if (channel === "base_overrides" && domain && !key) return domain;
  return path;
}
function ladderTierForRung(rung) {
  if (!rung) return void 0;
  return LADDER_TIERS.includes(rung) ? rung : void 0;
}
function policyFocusSearch(settings) {
  const rows = [...new Set(settings.map((s) => policyRowFor(s.path)))].filter(Boolean);
  const tiers = new Set(settings.map((s) => ladderTierForRung(s.rung)));
  const only = tiers.size === 1 ? [...tiers][0] : void 0;
  return {
    ...rows.length ? { policy: rows.join(",") } : {},
    ...only ? { policyScope: only } : {}
  };
}
function domainLabel(domain) {
  return DOMAIN_LABELS[domain] ?? domain;
}
function originOf(ladder, path) {
  return (ladder?.origins ?? []).find((o) => o.path === path);
}
function knobStatus(origin, viewingTier) {
  if (!origin) return "default";
  if (origin.tier === "base") return "default";
  return origin.tier === viewingTier ? "own" : "inherited";
}
function explainKnob(ladder, path, tierLabels, kind) {
  const origin = originOf(ladder, path);
  if (!origin) return "Not set at any scope \u2014 the platform default applies.";
  if (origin.tier === "base") {
    return `${formatKnobValue(origin.value, kind, path)} \u2014 the platform default. No scope has overridden it.`;
  }
  const label = tierLabels[origin.tier] ?? origin.tier;
  const inherited = (origin.overridden_tiers ?? []).filter(
    (t) => t !== origin.tier
  );
  const tail = inherited.length ? ` (overriding ${inherited.map((t) => tierLabels[t] ?? t).join(", ")})` : "";
  return `${formatKnobValue(origin.value, kind, path)} \u2014 set at ${label}${tail}.`;
}
function formatKnobValue(value, kind, knob) {
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (Array.isArray(value) && (kind === "lanes" || value.every(isForensicLane))) return forensicLanesLabel(value);
  if (typeof value === "number") {
    if (kind === "count") return `${value} source${value === 1 ? "" : "s"}`;
    if (kind === "points") return `${value} point${value === 1 ? "" : "s"}`;
    if (kind === "number") return String(value);
    if (kind === "days") {
      return value <= 0 ? "Off" : `${value} day${value === 1 ? "" : "s"}`;
    }
    if (kind === "probability") return `${Math.round(value * 100)}%`;
    return value >= 0 && value <= 1 ? `${Math.round(value * 100)}%` : String(value);
  }
  if (typeof value === "string") {
    const choice = knob ? knobChoices(knob).find((c) => c.value === value) : void 0;
    if (choice) return choice.label;
    return GATE_ACTION_LABELS[value] ?? value;
  }
  return JSON.stringify(value ?? null);
}
function domainsToShow(ladder, vocabularyDomains) {
  const seen = new Set(vocabularyDomains);
  for (const origin of ladder?.origins ?? []) {
    const d = floorDomain(origin.path) ?? actionDomain(origin.path);
    if (d) seen.add(d);
  }
  return [...seen].sort((a, b) => domainLabel(a).localeCompare(domainLabel(b)));
}
function buildSection(edits) {
  const section = {};
  for (const [path, value] of Object.entries(edits)) {
    if (value === void 0 || value === null || value === "") continue;
    setPath(section, policyPathParts(path), value);
  }
  return section;
}
function setPath(target, parts, value) {
  const [head, ...rest] = parts;
  if (!head) return;
  if (rest.length === 0) {
    target[head] = value;
    return;
  }
  const existing = target[head];
  const bucket = existing && typeof existing === "object" && !Array.isArray(existing) ? { ...existing } : {};
  setPath(bucket, rest, value);
  target[head] = bucket;
}
function deletePath(target, parts) {
  const [head, ...rest] = parts;
  if (!head || !(head in target)) return;
  if (rest.length === 0) {
    delete target[head];
    return;
  }
  const existing = target[head];
  if (!existing || typeof existing !== "object" || Array.isArray(existing)) return;
  const bucket = { ...existing };
  deletePath(bucket, rest);
  if (Object.keys(bucket).length === 0) delete target[head];
  else target[head] = bucket;
}
function policyRowGroup(row) {
  if (row.enforcementStatus === "not_enforced") return "unenforced";
  if (row.platform !== void 0 && row.platform.value === null && row.floor == null && !row.action) {
    return "reference";
  }
  return "tunable";
}
function actionNeverFires(domain, authoredFloor, platformFloors) {
  const entry = platformFloors?.[domain];
  if (entry && (entry.value === null || entry.gates_at_zero)) return false;
  const authored = authoredFloor === void 0 || authoredFloor === null || authoredFloor === "" ? void 0 : Number(authoredFloor);
  if (authored !== void 0) return Number.isFinite(authored) && authored <= 0;
  return entry ? entry.value !== null && entry.value <= 0 : false;
}
function offeredGateActions(supported, current) {
  if (!supported) return { offered: [...GATE_ACTIONS], unsupported: false };
  return {
    offered: GATE_ACTIONS.filter((a) => supported.includes(a) || a === current),
    unsupported: Boolean(current && !supported.includes(current))
  };
}
function enforcementFor(map, domain, tier) {
  const entry = map?.[domain];
  const rung = entry?.by_rung?.[tier];
  return entry && rung ? { ...entry, status: rung.status, reason: rung.reason } : entry;
}
var DOMAIN_CHANNELS = ["floors", "gate_actions", "base_overrides"];
var CHANNEL_NOUNS = {
  floors: "a minimum confidence",
  gate_actions: "what happens below the floor",
  base_overrides: "a weight table"
};
function inertReason(path, enforcement) {
  const [channel, domain, key, ...rest] = path.split(".");
  if (!enforcement || !domain || !DOMAIN_CHANNELS.includes(channel)) return null;
  const entry = enforcement[domain];
  if (!entry) return null;
  if (entry.status === "not_enforced") return entry.reason;
  if (channel === "base_overrides" && key && rest.length === 0 && entry.inert_keys?.includes(key)) {
    return entry.inert_reason ?? entry.reason;
  }
  return null;
}
function inertOrigins(ladder, enforcement) {
  const out = [];
  for (const origin of ladder?.origins ?? []) {
    if (origin.tier === "base") continue;
    const reason = inertReason(origin.path, enforcement);
    if (reason) out.push({ origin, reason });
  }
  return out;
}
function settingLabel(path) {
  const forensic = forensicCheckPath(path);
  if (forensic) {
    if (forensic.field === "enabled") return "Switched on";
    if (forensic.field === "lanes") return "Runs in";
    if (forensic.field === "param") return forensicParamLabel(forensic.param);
    return forensic.field === "params" ? "Thresholds" : "Settings";
  }
  const [channel, , key] = path.split(".");
  if (channel === "floors") return "Minimum confidence";
  if (channel === "gate_actions") return "Below the floor";
  if (channel === "base_overrides" && key) return `${key} weight`;
  if (channel === "risk_signals") return key === "enabled" ? "Switched on" : "Points";
  if (channel === "risk_policies") return key === "enabled" ? "Switched on" : "Ceiling";
  return knobLabel(path);
}
function sectionSettings(section) {
  return [...sectionKnobs(section).keys()].map(describeSetting);
}
function describeSetting(dotted) {
  const forensic = forensicCheckPath(dotted);
  if (forensic) {
    return `Forensic check ${forensicCheck(forensic.checkId)?.title ?? forensic.checkId}: ${settingLabel(dotted)}`;
  }
  const [channel, domain, key] = dotted.split(".");
  if ((channel === "floors" || channel === "gate_actions") && domain) {
    return `${domainLabel(domain)}: ${settingLabel(dotted)}`;
  }
  if (channel === "base_overrides" && domain && key) return `${domainLabel(domain)}: ${key} weight`;
  if (channel === "base_overrides" && domain) return `${domainLabel(domain)}: weight table`;
  if (channel === "tier_cutoffs" && domain) return CUTOFF_LABELS[domain] ?? `${domain} cutoff`;
  if (channel === "risk_signals" && domain) return `Risk signal ${domain}: ${settingLabel(dotted)}`;
  if (channel === "risk_policies" && domain) return `Risk rule ${domain}: ${settingLabel(dotted)}`;
  return knobLabel(dotted);
}
function formatSettingValue(dotted, raw) {
  const [channel] = dotted.split(".");
  const trimmed = raw.trim();
  const parsed = trimmed === "true" ? true : trimmed === "false" ? false : trimmed !== "" && Number.isFinite(Number(trimmed)) ? Number(trimmed) : raw;
  const kind = channel === "floors" || channel === "tier_cutoffs" || channel === "base_overrides" ? "probability" : channel === "gate_actions" ? void 0 : knobKind(dotted);
  return formatKnobValue(parsed, kind, dotted);
}
function sectionKnobs(section) {
  const out = /* @__PURE__ */ new Map();
  const walk = (value, path) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      for (const [key, child] of Object.entries(value)) {
        walk(child, [...path, key]);
      }
      return;
    }
    if (value === void 0 || value === null || value === "" || path.length === 0) return;
    out.set(path.join("."), value);
  };
  walk(section ?? {}, []);
  return out;
}
function sectionPaths(section) {
  return [...sectionKnobs(section).keys()].sort();
}
function authorsNothing(section) {
  return sectionKnobs(section).size === 0;
}
function changedKnobs(before, after) {
  const from = sectionKnobs(before);
  const to = sectionKnobs(after);
  const out = /* @__PURE__ */ new Set();
  for (const [path, value] of to) {
    if (!from.has(path) || !sameKnobValue(value, from.get(path))) out.add(path);
  }
  for (const path of from.keys()) if (!to.has(path)) out.add(path);
  return [...out].sort();
}
function sameKnobValue(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item) => b.includes(item));
  }
  return Object.is(a, b);
}
function sectionsEqual(a, b) {
  return changedKnobs(a, b).length === 0;
}
function unrecordedKnobs(asked, recorded) {
  if (recorded === void 0 || recorded === null) return [];
  const landed = sectionKnobs(recorded);
  return [...sectionKnobs(asked).keys()].filter((path) => !landed.has(path)).sort();
}
function localProblems(section, vocabularyDomains, enforcement, platformFloors, supportedActions) {
  const problems = [];
  const known = new Set(vocabularyDomains);
  const floors = section.floors ?? {};
  const actions = section.gate_actions ?? {};
  for (const [channel, bucket] of [
    ["floors", floors],
    ["gate_actions", actions]
  ]) {
    for (const domain of Object.keys(bucket)) {
      if (!known.has(domain)) {
        problems.push(`${channel}.${domain} is not a domain the platform resolves.`);
      }
    }
  }
  for (const [domain, action] of Object.entries(actions)) {
    if (!GATE_ACTIONS.includes(String(action))) {
      problems.push(`${domain}: "${String(action)}" is not one of ${GATE_ACTIONS.join(", ")}.`);
    }
  }
  for (const domain of Object.keys(actions)) {
    if (!actionNeverFires(domain, floors[domain], platformFloors)) continue;
    const authored = floors[domain] !== void 0 && floors[domain] !== null && floors[domain] !== "";
    problems.push(
      authored ? `${domainLabel(domain)}: you chose what happens below the floor, but the floor is 0 \u2014 so nothing is ever below it.` : `${domainLabel(domain)}: you chose what happens below the floor, but this domain has no floor by default \u2014 set one above 0, or nothing is ever below it.`
    );
  }
  for (const [domain, action] of Object.entries(actions)) {
    const supported = supportedActions?.[domain];
    if (!supported?.length) continue;
    const value = String(action);
    if (!GATE_ACTIONS.includes(value)) continue;
    if (supported.includes(value)) continue;
    problems.push(
      `${domainLabel(domain)}: this check can only ${supported.map((a) => ACTION_VERBS[a] ?? a).join(" or ")}, so "${GATE_ACTION_LABELS[value] ?? value}" behaves as the nearest weaker one it supports.`
    );
  }
  problems.push(...forensicCheckProblems(section[FORENSIC_CHECKS_SECTION]));
  for (const [path, value] of sectionKnobs(
    Object.fromEntries(RISK_SECTIONS.map((k) => [k, section[k]]))
  )) {
    if (!isRiskPoints(path)) continue;
    const n = coerceNumber(value);
    if (n == null || n < RISK_POINTS_MIN || n > RISK_POINTS_MAX) {
      problems.push(`${describeSetting(path)} must be a number from ${RISK_POINTS_MIN} to ${RISK_POINTS_MAX}.`);
    }
  }
  for (const channel of DOMAIN_CHANNELS) {
    const bucket = section[channel];
    if (!bucket || typeof bucket !== "object" || Array.isArray(bucket)) continue;
    for (const [domain, value] of Object.entries(bucket)) {
      if (value === void 0 || value === null || value === "") continue;
      const table = typeof value === "object" && !Array.isArray(value) ? Object.keys(value) : null;
      if (table && table.length === 0) continue;
      const whole = inertReason(`${channel}.${domain}`, enforcement);
      if (whole) {
        problems.push(
          `${domainLabel(domain)}: ${CHANNEL_NOUNS[channel]} is saved, but nothing evaluates it \u2014 ${whole} Remove it to save this scope.`
        );
        continue;
      }
      for (const key of table ?? []) {
        const reason = inertReason(`${channel}.${domain}.${key}`, enforcement);
        if (reason) {
          problems.push(
            `${domainLabel(domain)}: the ${key} weight is saved, but nothing evaluates it \u2014 ${reason} Remove it to save this scope.`
          );
        }
      }
    }
  }
  return problems;
}

// src/evidence/source.ts
function policyAtScope(ladder, tier) {
  const scoped = ladder.scope_views?.[tier];
  if (scoped) return { ...ladder, ...scoped };
  const rank = LADDER_TIERS.indexOf(tier);
  if (ladder.origins.some((o) => {
    const originRank = LADDER_TIERS.indexOf(o.tier);
    return originRank < 0 || originRank > rank;
  })) return null;
  return ladder;
}
var policyListeners = globalSingleton(
  "evidence/policy-listeners",
  () => /* @__PURE__ */ new WeakMap()
);
function whoseLadder(options) {
  const named = Object.entries(options.tenancy ?? {}).filter(
    (entry) => typeof entry[1] === "string" && entry[1] !== ""
  );
  if (options.platform) {
    if (named.length) throw new Error("The platform view names no tenancy.");
    return { view: PLATFORM_VIEW };
  }
  return Object.fromEntries(named);
}
var PLATFORM_VIEW = "platform";
function createApiEvidencePolicySource(client, options = {}) {
  const at = (verb, sub) => options.url ? options.url(verb, sub) : sub === "/preview" ? METADATA_PATHS.evidencePolicyPreview : METADATA_PATHS.evidencePolicy;
  const who = whoseLadder(options);
  let listeners = policyListeners.get(client);
  if (!listeners) {
    listeners = /* @__PURE__ */ new Set();
    policyListeners.set(client, listeners);
  }
  const subscribers = listeners;
  return {
    subscribe(listener) {
      subscribers.add(listener);
      return () => {
        subscribers.delete(listener);
      };
    },
    async getLadder() {
      return unwrapData(
        await client.get(at("GET", ""), Object.keys(who).length ? { query: who } : void 0)
      );
    },
    async preview(tier, section) {
      return unwrapData(
        await client.post(at("ANY", "/preview"), { section }, { query: { ...who, tier } })
      );
    },
    async save(tier, section, note) {
      const recorded = unwrapData(
        await client.put(at("ANY", ""), { section, note }, { query: { ...who, tier } })
      );
      const missing = unrecordedKnobs(section, recorded?.section);
      if (missing.length) {
        throw new Error(
          `The server accepted the save but did not record ${missing.map((path) => `${settingLabel(path)} (${path})`).join(", ")}. Nothing was changed at this scope.`
        );
      }
      client.invalidateMatching("");
      subscribers.forEach((listener) => listener());
    },
    async clear(tier) {
      unwrapData(await client.delete(at("ANY", ""), { query: { ...who, tier } }));
      client.invalidateMatching("");
      subscribers.forEach((listener) => listener());
    }
  };
}

export { ACTION_VERBS, CUTOFF_HELP, CUTOFF_LABELS, DOMAIN_HELP, DOMAIN_LABELS, FORENSIC_CHECKS_SECTION, GATE_ACTIONS, GATE_ACTION_HELP, GATE_ACTION_LABELS, KNOB_CHOICES, KNOB_HELP, KNOB_KINDS, KNOB_LABELS, KNOB_ORDER, LADDER_TIERS, PLATFORM_VIEW, RISK_POINTS_FIELD, RISK_POINTS_MAX, RISK_POINTS_MIN, RISK_SECTIONS, WEIGHT_TABLE_TITLES, WRITABLE_TIERS, actionDomain, actionNeverFires, activitySummary, activityWithheld, authorsNothing, baseOverridePath, buildSection, candidateSection, changedKnobs, createApiEvidencePolicySource, cutoffKey, describeSetting, domainLabel, domainsToShow, enforcementFor, explainKnob, floorDomain, forensicCheckPath, forensicCheckProblems, forensicEnabledPath, forensicLanesPath, forensicParamLabel, forensicParamPath, formatKnobValue, formatSettingValue, inertOrigins, inertReason, inheritedValue, isRiskPoints, knobChoices, knobKind, knobLabel, knobStatus, knobsToShow, ladderTierForRung, localProblems, numericKnobValue, offeredGateActions, originOf, ownSectionOf, policyAtScope, policyFocusSearch, policyPathParts, policyRowFor, policyRowGroup, sectionKnobs, sectionPaths, sectionSettings, sectionsEqual, settingLabel, shadowedSettings, unrecordedKnobs, weightRows, weightTableTitle, whoseLadder };
//# sourceMappingURL=chunk-7WFU2LWK.js.map
//# sourceMappingURL=chunk-7WFU2LWK.js.map